import type { HttpClient } from '../http/HttpClient.ts';

export interface AgentKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  projectIds: string[] | null;
  expiresAt: string | null;
  revokedAt: string | null;
  graceUntil: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  lastUsedClient: string | null;
  createdAt: string;
}

export interface Agent {
  id: string;
  name: string;
  description: string;
  disabled: boolean;
  /** "Others can send it work": other people's @mentions reach it. */
  acceptsMentions: boolean;
  /** Waiting on its inbox now (within 2 minutes), and when it last did. */
  listening: boolean;
  lastListenedAt: string | null;
  createdAt: string;
  keys: AgentKey[];
  /** Organizations whose admins turned it off. */
  blockedIn: string[];
}

export interface ProjectChoice {
  id: string;
  key: string;
  name: string;
  org: { slug: string; name: string };
}

export interface OrgAgent {
  id: string;
  name: string;
  owner: { id: string; name: string };
  disabled: boolean;
  blocked: boolean;
  lastUsedAt: string | null;
}

export interface NewKey {
  name: string;
  scopes: string[];
  projectIds: string[] | null;
  expiresAt: string | null;
}

/** Your agents and their keys; an organization's view of its members' agents. */
export class AgentsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  list(): Promise<{ agents: Agent[]; projects: ProjectChoice[] }> {
    return this.#http.get('/me/agents');
  }

  create(input: { name: string; description: string }): Promise<{ agent: Agent }> {
    return this.#http.post('/me/agents', input);
  }

  update(
    id: string,
    patch: { name?: string; description?: string; disabled?: boolean; acceptsMentions?: boolean },
  ): Promise<void> {
    return this.#http.patch(`/me/agents/${encodeURIComponent(id)}`, patch);
  }

  remove(id: string): Promise<void> {
    return this.#http.delete(`/me/agents/${encodeURIComponent(id)}`);
  }

  /** The key itself is in the answer this once. */
  createKey(agentId: string, input: NewKey): Promise<{ key: string; view: AgentKey }> {
    return this.#http.post(`/me/agents/${encodeURIComponent(agentId)}/keys`, input);
  }

  rollKey(keyId: string, graceHours: number): Promise<{ key: string; view: AgentKey }> {
    return this.#http.post(`/me/agent-keys/${encodeURIComponent(keyId)}/roll`, { graceHours });
  }

  revokeKey(keyId: string): Promise<void> {
    return this.#http.delete(`/me/agent-keys/${encodeURIComponent(keyId)}`);
  }

  // ---- webhook (for starting an agent without a runner polling) ----

  webhook(agentId: string): Promise<{ webhook: Webhook | null }> {
    return this.#http.get(`${hook(agentId)}`);
  }

  /** A new webhook's signing secret is in the answer this once. */
  setWebhook(agentId: string, url: string): Promise<{ webhook: Webhook; secret: string | null }> {
    return this.#http.put(hook(agentId), { url });
  }

  rotateWebhookSecret(agentId: string): Promise<{ secret: string }> {
    return this.#http.post(`${hook(agentId)}/rotate`);
  }

  testWebhook(agentId: string): Promise<void> {
    return this.#http.post(`${hook(agentId)}/test`);
  }

  resumeWebhook(agentId: string): Promise<void> {
    return this.#http.post(`${hook(agentId)}/resume`);
  }

  removeWebhook(agentId: string): Promise<void> {
    return this.#http.delete(hook(agentId));
  }

  inOrg(org: string): Promise<{ agents: OrgAgent[] }> {
    return this.#http.get(`/orgs/${encodeURIComponent(org)}/agents`);
  }

  setBlocked(org: string, agentId: string, blocked: boolean): Promise<void> {
    return this.#http.put(
      `/orgs/${encodeURIComponent(org)}/agents/${encodeURIComponent(agentId)}/block`,
      { blocked },
    );
  }
}

const hook = (agentId: string) => `/me/agents/${encodeURIComponent(agentId)}/webhook`;

export interface WebhookDelivery {
  id: string;
  event: 'agent.message' | 'ping';
  status: 'pending' | 'sending' | 'sent' | 'failed';
  attempts: number;
  lastStatus: number | null;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface Webhook {
  url: string;
  paused: boolean;
  pausedAt: string | null;
  failuresInRow: number;
  createdAt: string;
  deliveries: WebhookDelivery[];
}
